/**
 * Decide a recipe's language from its own text. The server, the tooling and
 * the client's saved-recipe writers all use this; `src/utils/detectLanguage.ts`
 * (title only, substring matches) is no longer what tags a recipe.
 *
 * Whole words only. Substring matching is what read "champignons" as English
 * (it contains "pig" and "ham" in other lists) and left 13 catalogue recipes
 * tagged unknown (#737).
 */
export interface LanguageGuess {
  lang: 'en' | 'fr' | 'unknown';
  evidence: string;
  fr: number;
  en: number;
}

const FR = [
  'de', 'du', 'des', 'la', 'le', 'les', 'et', 'au', 'aux', 'avec', 'dans', 'pour', 'une', 'un',
  'sel', 'poivre', 'beurre', 'farine', 'huile', 'oignon', 'ail', 'tasse', 'cuillère', 'soupe', 'thé',
  'poulet', 'boeuf', 'bœuf', 'porc', 'crème', 'lait', 'oeuf', 'œuf', 'oeufs', 'œufs',
  'mélanger', 'ajouter', 'cuire', 'faire', 'chauffer', 'servir', 'jusqu', 'environ', 'four', 'sur',
  'ou', 'puis', 'bien', 'feu', 'moyen', 'gousse', 'gousses', 'haché', 'hachée', 'coupé', 'coupée',
  'émincé', 'râpé', 'minutes',
];
const EN = [
  'the', 'and', 'with', 'of', 'a', 'in', 'to', 'for', 'or', 'salt', 'pepper', 'butter', 'flour',
  'oil', 'onion', 'garlic', 'cup', 'cups', 'tablespoon', 'tablespoons', 'teaspoon', 'teaspoons',
  'tbsp', 'tsp', 'chicken', 'beef', 'pork', 'cream', 'milk', 'egg', 'eggs', 'mix', 'add', 'cook',
  'heat', 'serve', 'until', 'about', 'oven', 'over', 'then', 'well', 'medium', 'clove', 'cloves',
  'chopped', 'sliced', 'minced', 'grated', 'roasted', 'baked', 'grilled', 'salad', 'soup', 'stew',
  'minutes',
];

const frSet = new Set(FR);
const enSet = new Set(EN);

export function detectLanguageFromText(parts: Array<string | undefined | null>): LanguageGuess {
  const text = parts.filter((p): p is string => typeof p === 'string').join(' ').toLowerCase();
  // Split on anything that is not a letter, keeping accented letters together.
  const words = text.split(/[^\p{L}]+/u).filter(Boolean);
  let fr = 0;
  let en = 0;
  const hits: string[] = [];
  for (const w of words) {
    // "minutes" is in both lists on purpose: it cancels out.
    const isFr = frSet.has(w);
    const isEn = enSet.has(w);
    if (isFr && isEn) continue;
    if (isFr) { fr++; if (hits.length < 6) hits.push(`fr:${w}`); }
    else if (isEn) { en++; if (hits.length < 6) hits.push(`en:${w}`); }
  }
  let lang: LanguageGuess['lang'] = 'unknown';
  if (fr + en > 0 && Math.abs(fr - en) >= 2) lang = fr > en ? 'fr' : 'en';
  return { lang, fr, en, evidence: `fr=${fr} en=${en} [${hits.join(' ')}]` };
}

/**
 * The `originalLanguage` field for a recipe document, decided from its title,
 * ingredient lines and steps. Nothing when the text does not decide: 'unknown'
 * is never stored, the field's absence already says it.
 */
export function recipeLanguageField(
  parts: Array<string | undefined | null>,
): { originalLanguage?: 'en' | 'fr' } {
  const { lang } = detectLanguageFromText(parts);
  return lang === 'unknown' ? {} : { originalLanguage: lang };
}
